import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ShieldCheck } from "lucide-react";
import { scrollToId, BRAND } from "@/lib/site";

const LINKS = [
  { label: "Home", key: "home", id: "home" },
  { label: "Services", key: "services", route: "/services" },
  { label: "Calculators", key: "calculator", route: "/calculators" },
  { label: "Resources", key: "journal", route: "/journal" },
  { label: "Disclosure", key: "disclosure", route: "/disclosure" },
];

const ROUTE_ACTIVE = {
  "/services": "services",
  "/calculators": "calculator",
  "/journal": "journal",
  "/disclosure": "disclosure",
};

const EASE = [0.22, 1, 0.36, 1];

export const Wordmark = ({ onClick }) => (
  <button
    type="button"
    data-testid="nav-wordmark"
    onClick={onClick}
    className="flex items-center gap-2.5 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
    aria-label="HRKAPITAL — home"
  >
    <img src="/assets/logo-mark.png" alt="HRKAPITAL logo" className="h-8 w-8 object-contain" />
    <span className="font-sans text-sm font-semibold tracking-[0.32em] text-[#10202E]">HRKAPITAL</span>
  </button>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState("home");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const active = ROUTE_ACTIVE[pathname] || (pathname === "/" ? section : "");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (pathname !== "/") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setSection(entry.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    ["home", "contact"].forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (window.__lenis) {
      if (open) window.__lenis.stop();
      else window.__lenis.start();
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = ({ route, id }) => {
    const delay = open ? 350 : 0;
    setOpen(false);
    if (route) {
      setTimeout(() => navigate(route), delay);
      return;
    }
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), delay + 600);
      return;
    }
    setTimeout(() => scrollToId(id), delay);
  };

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter,box-shadow] duration-500 ${
          scrolled
            ? "border-b border-[#10202E]/[0.07] bg-[#F8F7F3]/75 shadow-[0_8px_32px_rgba(16,32,46,0.05)] backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-6">
          <Wordmark onClick={() => go(LINKS[0])} />

          <nav className="hidden items-center gap-10 lg:flex" aria-label="Primary">
            {LINKS.map((link) => (
              <button
                key={link.key}
                type="button"
                data-testid={`nav-link-${link.key}`}
                onClick={() => go(link)}
                aria-current={active === link.key ? "true" : undefined}
                className={`group relative py-2 text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                  active === link.key ? "text-[#10202E]" : "text-[#4E6A82] hover:text-[#10202E]"
                }`}
              >
                {link.label}
                {active === link.key ? (
                  <motion.span
                    layoutId="nav-active-indicator"
                    data-testid="nav-active-indicator"
                    transition={{ duration: 0.45, ease: EASE }}
                    className="absolute -bottom-[2px] left-0 right-0 h-[2px] rounded-full bg-[#19B77A]"
                  />
                ) : (
                  <span className="absolute -bottom-[2px] left-0 h-[2px] w-0 rounded-full bg-[#19B77A]/50 transition-[width] duration-300 group-hover:w-full" />
                )}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              type="button"
              data-testid="nav-cta-button"
              onClick={() => go({ id: "contact" })}
              className="group hidden items-center gap-2 rounded-full bg-[#19B77A] px-5 py-2.5 text-[13px] font-semibold text-[#06251A] shadow-[0_4px_18px_rgba(25,183,122,0.28)] transition-[background-color,box-shadow,transform] duration-300 hover:bg-[#15a06a] hover:shadow-[0_6px_24px_rgba(25,183,122,0.38)] active:scale-[0.98] lg:inline-flex"
            >
              Start a conversation
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              type="button"
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              className="rounded-full border border-[#10202E]/15 p-2.5 text-[#10202E] transition-colors duration-300 hover:border-[#19B77A]/60 lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[70] flex flex-col bg-[#F8F7F3]/[0.98] backdrop-blur-2xl lg:hidden"
          >
            <div className="flex h-[76px] items-center justify-between px-6">
              <Wordmark onClick={() => go(LINKS[0])} />
              <motion.button
                type="button"
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.35, ease: EASE }}
                className="rounded-full border border-[#10202E]/15 p-2.5 text-[#10202E]"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </motion.button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {LINKS.map((link, i) => (
                <motion.button
                  key={link.key}
                  type="button"
                  data-testid={`mobile-nav-link-${link.key}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.6, ease: EASE }}
                  onClick={() => go(link)}
                  className={`flex items-center justify-between border-b border-[#10202E]/[0.08] py-5 text-left font-display text-4xl transition-colors duration-300 ${
                    active === link.key ? "text-[#10202E]" : "text-[#10202E]/80 hover:text-emerald-700"
                  }`}
                >
                  {link.label}
                  {active === link.key && (
                    <motion.span
                      layoutId="mobile-nav-active"
                      className="h-2 w-2 rounded-full bg-[#19B77A]"
                      aria-hidden="true"
                    />
                  )}
                </motion.button>
              ))}
              <motion.button
                type="button"
                data-testid="mobile-nav-cta"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 + LINKS.length * 0.07, duration: 0.6, ease: EASE }}
                onClick={() => go({ id: "contact" })}
                className="group mt-8 inline-flex items-center justify-center gap-2 self-start rounded-full bg-[#19B77A] px-7 py-4 text-sm font-semibold text-[#06251A] shadow-[0_6px_24px_rgba(25,183,122,0.32)] transition-[background-color,box-shadow] duration-300 hover:bg-[#15a06a]"
              >
                Start a conversation
                <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </motion.button>
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center gap-2.5 px-8 pb-10"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-emerald-700/80">
                {BRAND.badge}
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
